/**
 * Settings Service for App-client
 * Fetches and manages app display settings from backend
 */

import { withApiErrorHandling, buildApiEndpoint } from '../utils/apiHelpers'
import apiClient from '../config/api'

// Default settings to use if fetch fails
const DEFAULT_SETTINGS = {
  APP_DISPLAY_SERVICOS: true,
  APP_DISPLAY_PRODUTOS: true,
  APP_DISPLAY_ASSINATURAS: true,
}

/**
 * Fetch all settings as a dictionary with typed values
 * @returns {Promise<Object>} Settings dictionary
 */
export const getAllSettings = async () => {
  const endpoint = buildApiEndpoint('settings/values')

  return withApiErrorHandling(
    () => apiClient.get(endpoint),
    {
      defaultValue: DEFAULT_SETTINGS,
      transformData: (data) => ({
        ...DEFAULT_SETTINGS, // Ensure defaults for any missing keys
        ...data
      }),
      logErrors: false
    }
  )
}

/**
 * Fetch a specific setting by key
 * @param {string} key - Setting key
 * @returns {Promise<any>} Setting value (typed)
 */
export const getSetting = async (key) => {
  const endpoint = buildApiEndpoint(`settings/${key}`)

  return withApiErrorHandling(
    () => apiClient.get(endpoint),
    {
      defaultValue: DEFAULT_SETTINGS[key] ?? null,
      transformData: (data) => data.value,
      logErrors: false
    }
  )
}

/**
 * Helper to check if a setting value is truthy (handles boolean, string, undefined)
 * @param {any} value - The setting value
 * @returns {boolean} True if the setting is enabled
 */
const isSettingEnabled = (value) => {
  // If undefined or null, default to true (show tab)
  if (value === undefined || value === null) return true
  // Handle boolean
  if (typeof value === 'boolean') return value
  // Handle string
  if (typeof value === 'string') {
    return value.toLowerCase() !== 'false' && value !== '0' && value.toLowerCase() !== 'no'
  }
  // Handle number
  if (typeof value === 'number') return value !== 0
  // Default to true
  return true
}

/**
 * Get app display settings (convenience method)
 * @returns {Promise<Object>} Display settings for tabs
 */
export const getAppDisplaySettings = async () => {
  const settings = await getAllSettings()

  return {
    showServicos: isSettingEnabled(settings.APP_DISPLAY_SERVICOS),
    showProdutos: isSettingEnabled(settings.APP_DISPLAY_PRODUTOS),
    showAssinaturas: isSettingEnabled(settings.APP_DISPLAY_ASSINATURAS),
  }
}

export const settingsService = {
  getAllSettings,
  getSetting,
  getAppDisplaySettings,
}

export default settingsService
